'use client'
import { useRef, useEffect } from 'react'
import { motion, useInView, type Variants } from 'framer-motion'

const textVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2, delayChildren: 0.2 } },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
}

export function BrandVideoSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const isInView = useInView(sectionRef, { once: false, margin: '-15%' })

  useEffect(() => {
    if (!videoRef.current) return
    if (isInView) {
      videoRef.current.play().catch(() => {})
    } else {
      videoRef.current.pause()
    }
  }, [isInView])

  return (
    <section ref={sectionRef} className="relative overflow-hidden" style={{ minHeight: '70vh' }}>
      <video
        ref={videoRef}
        src="/videos/brand-cinematic.mp4"
        muted
        playsInline
        loop
        preload="none"
        className="absolute inset-0 w-full h-full object-cover"
        aria-hidden="true"
      />

      {/* Gradient overlay — bottom-heavy so text stays readable */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.3) 40%, rgba(15,23,42,0.75) 100%)',
        }}
      />

      <motion.div
        className="relative z-10 flex flex-col items-center justify-center text-center page-container py-32 gap-5"
        variants={textVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        <motion.p
          variants={itemVariants}
          className="text-sm font-semibold tracking-widest uppercase text-[color:var(--accent)] opacity-90"
        >
          Northeast Corridor · Coast to Coast
        </motion.p>

        <motion.h2
          variants={itemVariants}
          className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight max-w-3xl leading-tight"
        >
          Moving freight that keeps{' '}
          <span className="text-[color:var(--accent)]">America running.</span>
        </motion.h2>

        <motion.p
          variants={itemVariants}
          className="text-lg text-white/75 max-w-xl leading-relaxed"
        >
          Volvo and Freightliner equipment. Same-day dispatch. Real-time visibility from pickup to delivery.
        </motion.p>

        <motion.div variants={itemVariants}>
          <a
            href="#quote"
            className="glass-button px-10 py-4 text-base font-semibold rounded-xl inline-block"
          >
            Get a Quote
          </a>
        </motion.div>
      </motion.div>
    </section>
  )
}
